'use client'

import { HOTEL_MAX_NIGHTS } from '@/src/constants/hotels'
import { getFormattedDate } from '@/src/utils/date'
import dayjs from 'dayjs'
import { useLocale, useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useState } from 'react'

import HotelDateModifier, { HotelDateRange } from './hotelDateModifier'

interface HotelDateModifierWrapperProps {
    variant: 'mobile' | 'desktop'
    open: boolean
    /** Committed stay (ISO) — the draft is re-seeded from it on every open. */
    stayCheckIn: string
    stayCheckOut: string
    onClose: () => void
    onApply: (checkIn: Date, checkOut: Date) => void
    /**
     * Let the guest apply the stay already shown. The property page pre-fills dates but has
     * not searched them, so "unchanged" there still means "price these" — everywhere else a
     * no-op apply would re-run a search that already ran.
     */
    allowUnchanged?: boolean
    /** Rooms-and-guests control shown under the dates. Only the property (SEO) page has one. */
    guestsField?: React.ReactNode
}

const parseIsoDate = (value: string): Date | null => {
    const parsed = dayjs(value)
    return value && parsed.isValid() ? parsed.startOf('day').toDate() : null
}

// Logic layer for the details date picker: owns the draft range and which leg is being
// picked. Re-pricing is the page container's job (checkHotelAvailability).
const HotelDateModifierWrapper: React.FC<HotelDateModifierWrapperProps> = ({
    variant,
    open,
    stayCheckIn,
    stayCheckOut,
    onClose,
    onApply,
    allowUnchanged = false,
    guestsField
}) => {
    const t = useTranslations('hotels')
    const tCommon = useTranslations('common')
    const locale = useLocale()

    const [localRange, setLocalRange] = useState<HotelDateRange>({ from: null, to: null })
    const [activeCheckOut, setActiveCheckOut] = useState(false)
    const [today] = useState(() => dayjs().startOf('day').toDate())

    // Each fresh open restarts from the committed stay on the check-in leg, so an
    // abandoned draft never carries over.
    useEffect(() => {
        if (!open) return
        setLocalRange({ from: parseIsoDate(stayCheckIn), to: parseIsoDate(stayCheckOut) })
        setActiveCheckOut(false)
    }, [open, stayCheckIn, stayCheckOut])

    const handleDateClick = useCallback(
        (dates: HotelDateRange) => {
            if (!activeCheckOut) {
                if (!dates.from) return
                // The calendar collapses the range onto the day just picked; keep an existing
                // check-out only if it is still after the new check-in, then hand off.
                const keepsCheckOut = dates.to && dayjs(dates.to).isAfter(dayjs(dates.from), 'day')
                setLocalRange({ from: dates.from, to: keepsCheckOut ? dates.to : null })
                setActiveCheckOut(true)
                return
            }
            if (!dates.to) return
            setLocalRange(dates)
        },
        [activeCheckOut]
    )

    const nights = useMemo(() => {
        const { from, to } = localRange
        if (!from || !to) return 0
        return dayjs(to).startOf('day').diff(dayjs(from).startOf('day'), 'day')
    }, [localRange])

    // The two shared calendars disagree on `minDate`: the desktop one disables days strictly
    // before it, the mobile one also disables the day itself. Pass each the last day that
    // must not be selectable so both block a zero-night stay.
    const minDate = useMemo(() => {
        const { from } = localRange
        if (activeCheckOut && from) {
            return variant === 'desktop' ? dayjs(from).add(1, 'day').toDate() : from
        }
        return variant === 'desktop' ? today : dayjs(today).subtract(1, 'day').toDate()
    }, [activeCheckOut, localRange, variant, today])

    const formatLeg = useCallback(
        (date: Date | null) => (date ? getFormattedDate(date, 'DD MMM YYYY') : tCommon('selectDate')),
        [tCommon]
    )

    const isChanged =
        !!localRange.from &&
        !!localRange.to &&
        (getFormattedDate(localRange.from, 'YYYY-MM-DD') !== stayCheckIn ||
            getFormattedDate(localRange.to, 'YYYY-MM-DD') !== stayCheckOut)

    const isValidLength = nights > 0 && nights <= HOTEL_MAX_NIGHTS

    const handleApply = useCallback(() => {
        const { from, to } = localRange
        if (!from || !to) return
        onApply(from, to)
    }, [localRange, onApply])

    // Only says why the current selection can't be applied; the stay length is not repeated
    // here because the dates above already state it.
    const warningText = useMemo(
        () => (nights > HOTEL_MAX_NIGHTS ? t('maxNightsError', { nights: HOTEL_MAX_NIGHTS }) : ''),
        [nights, t]
    )

    return (
        <HotelDateModifier
            variant={variant}
            open={open}
            localRange={localRange}
            activeCheckIn={!activeCheckOut}
            activeCheckOut={activeCheckOut}
            checkOutEnabled={!!localRange.from}
            checkInText={formatLeg(localRange.from)}
            checkOutText={formatLeg(localRange.to)}
            warningText={warningText}
            isRtl={locale === 'ar'}
            title={t('updateDatesTitle')}
            checkInLabel={tCommon('checkIn')}
            checkOutLabel={tCommon('checkOut')}
            applyLabel={t('changeDatesApply')}
            guestsField={guestsField}
            guestsLabel={`${tCommon('rooms')} & ${tCommon('guest', { s: 's' })}`}
            canApply={(isChanged || allowUnchanged) && isValidLength}
            minDate={minDate}
            onCheckInClick={() => setActiveCheckOut(false)}
            onCheckOutClick={() => setActiveCheckOut(true)}
            onDateClick={handleDateClick}
            onClose={onClose}
            onApply={handleApply}
        />
    )
}

export { HotelDateModifierWrapper as HotelDateModifier }
export default HotelDateModifierWrapper
